/* import __COLOCATED_TEMPLATE__ from './payment-form.hbs'; */
import { action } from '@ember/object';
import { bannerError, callbackError } from 'client-portal/utils/error-handling';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import generateUUID from 'ember-simplepractice/utils/generate-uuid';
import styles from './payment-form.module.scss';

export default class SitePaymentLinkPaymentForm extends Component {
  styles = styles;
  @service stripePaymentModeElementService;
  @service currentPractice;
  @service stripe;
  @service store;
  @service router;
  @service localeManager;
  @service intl;

  @tracked stripeElement;
  @tracked card;
  @tracked isPersisting = false;

  @action
  initializeStripeElement() {
    this.stripeElement = this.stripePaymentModeElementService.createElement(
      this.stripe.stripe,
      this._stripeElementPaymentOptions()
    );
    this.card = this.store.createRecord('card', { isDefault: false });
  }

  @action
  async persistCardAndPayment() {
    const stripeError = await this._runStripeValidations();
    const cardError = await this._runCardValidations();

    if (stripeError || cardError) return;

    this.isPersisting = true;
    try {
      await this._createCardOnStripe(this.stripeElement);
      await this.store
        .adapterFor('payment-link')
        .createPaymentWithInvoiceIds(this.args.paymentLink.uuid, this.paymentData);
      this._reloadPaymentLink();
    } catch (error) {
      this._handlePaymentFailure(error);
    }
    this.isPersisting = false;
  }

  _reloadPaymentLink() {
    // Unload the payment link record to force a fresh reload
    this.store.unloadRecord(this.args.paymentLink);
    this.router.refresh();
  }

  async _runStripeValidations() {
    let { error } = await this.stripeElement.submit(); // Stripe named it 'submit' but it actually only validates the form

    return error;
  }

  async _runCardValidations() {
    this.card.set('_isDirty', true);

    return !this.card.name?.trim();
  }

  async _createCardOnStripe(elements) {
    const { paymentMethod } = await this.stripe.stripe.createPaymentMethod({
      elements,
      params: {
        /* eslint-disable camelcase */
        billing_details: {
          name: this.card.name,
        },
      },
    });
    const { card } = paymentMethod;

    this.card.setProperties({
      customStripeCustomerId: paymentMethod.customer,
      brand: card.brand,
      last4: card.last4,
      expMonth: card.exp_month,
      expYear: card.exp_year,
      paymentMethodId: paymentMethod.id,
    });
  }

  get paymentData() {
    return {
      data: {
        attributes: {
          amount: this.args.paymentLink.totalPayableAmount,
          maximumAmount: this.args.paymentLink.totalPayableAmount,
          amountType: 'total',
          idempotencyKey: generateUUID(),
          customCardToken: this.card.isDefault ? null : this.card.paymentMethodId,
          cardAttributes: this.card.isDefault ? this.cardAttributes : null,
        },
        type: 'payments',
      },
      meta: { invoiceIdsWithAmounts: this.invoiceIdsWithAmounts },
    };
  }

  get cardAttributes() {
    return {
      brand: this.card.brand,
      customStripeCustomerId: this.card.customStripeCustomerId,
      expMonth: this.card.expMonth,
      expYear: this.card.expYear,
      isDefault: this.card.isDefault,
      last4: this.card.last4,
      name: this.card.name,
      paymentMethodId: this.card.paymentMethodId,
    };
  }

  _handlePaymentFailure(err) {
    callbackError(err, message => {
      let title = this.intl.t('payment-link.payment-form.errors.payment-could-not-be-made');

      if (message.title.match(/Your account cannot currently make charges/)) {
        title = this.intl.t('payment-link.payment-form.errors.payment-could-not-be-processed');
        message.title = this.intl.t('payment-link.payment-form.errors.contact-provider');
      }

      bannerError(err, { title });
      return true;
    });
  }

  get invoiceIdsWithAmounts() {
    return this.args.paymentLink.allocatableInvoices.map(invoice => ({
      invoiceId: invoice.invoice_id,
      amount: invoice.payable_amount,
    }));
  }

  _stripeElementPaymentOptions() {
    return {
      amount: this.args.paymentLink.totalPayableAmount,
      currency: this.currentPractice.get('currency'),
      onBehalfOf: this.currentPractice.get('stripeConnectedAccountId'),
      paymentMethodTypes: ['card'],
      locale: this.localeManager.currentLocaleForStripe,
    };
  }
}
